import SEO from "@/components/SEO";

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for Asian Picks. How we collect, store, and protect your personal data — with your rights under GDPR and equivalent laws."
        path="/privacy"
      />
      <section className="bg-ink-950 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-emerald-700/25 blur-[120px]" />
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-gold-500/15 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gold-400 font-mono uppercase tracking-widest text-xs mb-3">
            Legal
          </div>
          <h1
            data-testid="privacy-title"
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl"
          >
            Privacy <span className="gold-text">Policy</span>
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-base sm:text-lg leading-relaxed">
            Your privacy matters. This policy explains what information we
            collect, why we collect it, and how we handle it.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-ink-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 text-ink-700 leading-relaxed">
          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              1. Who we are
            </h2>
            <p>
              Asian Picks ("we", "us", "our") operates the website{" "}
              <span className="font-mono text-emerald-700">
                asian-picks.com
              </span>{" "}
              and provides Asian Handicap football tipping services to
              paying subscribers. This Privacy Policy applies to all visitors
              to the website and to all subscribers of our services.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              2. Information we collect
            </h2>
            <p className="mb-3">
              We collect only the minimum amount of personal information
              needed to run the service. Specifically:
            </p>
            <ul className="space-y-2 list-disc pl-6">
              <li>
                <span className="font-semibold text-ink-900">Contact form data:</span>{" "}
                your name, email address, subject and message when you send us
                a message via the Contact page.
              </li>
              <li>
                <span className="font-semibold text-ink-900">Subscription data:</span>{" "}
                the email address to which tips are delivered, the package you
                purchased, and the payment method used (we do <em>not</em>{" "}
                store card numbers or crypto private keys).
              </li>
              <li>
                <span className="font-semibold text-ink-900">Technical data:</span>{" "}
                standard server logs such as IP address, browser type,
                referring page and timestamps, kept for security and
                troubleshooting.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              3. Why we collect it
            </h2>
            <ul className="space-y-2 list-disc pl-6">
              <li>To reply to your enquiries.</li>
              <li>To deliver the tips you have subscribed to.</li>
              <li>To keep accurate records of purchases for accounting and legal purposes.</li>
              <li>To protect the website from abuse, fraud and unauthorised access.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              4. Legal basis
            </h2>
            <p>
              We process your personal data on the basis of your consent (when
              you submit the contact form or subscribe), the performance of a
              contract (delivering tips you have paid for), and our legitimate
              interests (operating and protecting the website). We do not
              engage in profiling or automated decision-making that produces
              legal effects on you.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              5. How long we keep your data
            </h2>
            <p>
              Contact form submissions and subscription records are retained
              only as long as necessary for the purposes described above, or
              as required by applicable law and tax regulations. Technical
              logs are retained for a limited period (typically no longer
              than 90 days) before being deleted.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              6. Who we share your data with
            </h2>
            <p>
              We do not sell, rent or trade your personal data. We share it
              only with trusted service providers strictly necessary to
              operate the site — for example, our email delivery provider and
              our hosting provider. These providers process data on our behalf
              under contractual obligations that require them to keep your
              information secure and use it only for the purposes we
              specify.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              7. Cookies
            </h2>
            <p>
              Our website uses only strictly necessary cookies required for
              basic functionality (such as remembering that you have accepted
              a notice). We do not currently use advertising or third-party
              tracking cookies. If this changes in the future, we will update
              this policy and, where required, ask for your consent.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              8. Your rights
            </h2>
            <p className="mb-3">
              Subject to applicable data protection law, you have the right
              to:
            </p>
            <ul className="space-y-2 list-disc pl-6">
              <li>Request access to the personal data we hold about you.</li>
              <li>Ask us to correct data that is inaccurate or incomplete.</li>
              <li>Ask us to delete your data ("right to be forgotten").</li>
              <li>Object to or restrict our processing of your data.</li>
              <li>Withdraw consent at any time where processing is based on consent.</li>
              <li>Lodge a complaint with your local data protection authority.</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, email us at{" "}
              <a
                href="mailto:asianhpicks@gmail.com"
                className="text-emerald-700 underline hover:text-gold-600"
              >
                asianhpicks@gmail.com
              </a>
              . We will respond within 30 days.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              9. Security
            </h2>
            <p>
              We use industry-standard technical and organisational measures
              to protect your data against unauthorised access, alteration,
              disclosure or destruction. However, no method of internet
              transmission or electronic storage is 100% secure, and we
              cannot guarantee absolute security.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              10. Children
            </h2>
            <p>
              Our services are strictly for adults aged 18 or older (or the
              legal gambling age in your jurisdiction, whichever is higher).
              We do not knowingly collect personal data from minors. If you
              believe a minor has provided us with personal information,
              please contact us and we will delete it.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              11. Changes to this policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or in applicable law. The updated
              version will be posted on this page with a new effective date.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-2xl text-ink-900 mb-3">
              12. Contact us
            </h2>
            <p>
              Questions about this Privacy Policy or a request relating to
              your personal data? Email us at{" "}
              <a
                href="mailto:asianhpicks@gmail.com"
                className="text-emerald-700 underline hover:text-gold-600"
              >
                asianhpicks@gmail.com
              </a>
              .
            </p>
          </div>

          <p className="text-xs text-ink-500 font-mono pt-6 border-t border-ink-200">
            Last updated: 2023.
          </p>
        </div>
      </section>
    </div>
  );
}
